import { t } from "@airange/shared/i18n-admin-controls";
import type { ReactNode } from "react";
import {
  readAdminLegendSummaryCount,
  readAdminSemanticEnumLegendPresentation,
  readAdminSemanticEnumLegendSummaryCount,
  readAdminStructuredLegendSummaryCount,
} from "./admin-legend-summary";
import type {
  AdminFilterLegendGroupInput,
  AdminFlatEnumFilterLegendGroupInput,
  AdminLegendGroup,
  AdminLegendGroupInput,
  AdminLegendGroupItem,
  AdminSemanticEnumFilterLegendBucket,
  AdminSemanticEnumFilterLegendGroupInput,
  AdminStructuredFilterLegendGroupInput,
  AdminStructuredLegendSummary,
  AdminSummaryBarItem,
} from "./admin-legend-types";

export function toAdminLegendGroupItems(items: AdminSummaryBarItem[]): AdminLegendGroupItem[] {
  return items.map((item) => ({
    key: item.key,
    value: item.key,
    label: item.label,
    count: item.count ?? item.value ?? "-",
    semantic: item.semantic,
    color: item.color,
    active: item.active,
    onClick: item.onClick,
  }));
}

export function buildAdminLegendGroups(groups: AdminLegendGroupInput[]): AdminLegendGroup[] {
  return groups
    .filter((group) => group.items.length > 0)
    .map((group) => ({
      key: group.key,
      field: group.field,
      title: group.title,
      items: toAdminLegendGroupItems(group.items),
    }));
}

export function buildAdminFilterLegendGroups<TSummary extends object>(
  groups: AdminFilterLegendGroupInput<TSummary>[],
): AdminLegendGroup[] {
  return buildAdminLegendGroups(
    groups.map((group) => ({
      key: group.key,
      field: group.field,
      title: group.title,
      items: group.buckets.map((bucket) => {
        const onSelect = group.onSelect;
        const value = bucket.value;
        return {
          key: bucket.key ?? value ?? bucket.summaryKey,
          label: bucket.label,
          count: readAdminLegendSummaryCount(group.summary, bucket.summaryKey),
          semantic: bucket.semantic,
          active: value === undefined ? !group.activeValue : group.activeValue === value,
          onClick: onSelect ? () => onSelect(value) : undefined,
        };
      }),
    })),
  );
}

export function buildAdminStructuredFilterLegendGroups<
  TSummary extends AdminStructuredLegendSummary,
>(groups: AdminStructuredFilterLegendGroupInput<TSummary>[]): AdminLegendGroup[] {
  return buildAdminLegendGroups(
    groups.map((group) => {
      const summaryField = group.summaryField ?? group.field;
      return {
        key: group.key,
        field: group.field,
        title: group.title,
        items: group.buckets.map((bucket) => {
          const onSelect = group.onSelect;
          const value = bucket.value;
          return {
            key: bucket.key ?? value ?? "all",
            label: bucket.label,
            count: readAdminStructuredLegendSummaryCount(group.summary, summaryField, value),
            semantic: bucket.semantic,
            active: value === undefined ? !group.activeValue : group.activeValue === value,
            onClick: onSelect ? () => onSelect(value) : undefined,
          };
        }),
      };
    }),
  );
}

export function buildAdminFlatEnumFilterLegendGroups<TSummary extends object>(
  groups: AdminFlatEnumFilterLegendGroupInput<TSummary>[],
): AdminLegendGroup[] {
  return buildAdminLegendGroups(
    groups.map((group) => {
      const allBucket = group.allBucket ?? {
        key: "all",
        summaryKey: "total" as Extract<keyof TSummary, string>,
        semantic: "all" as const,
      };
      const onSelect = group.onSelect;
      return {
        key: group.key,
        field: group.field,
        title: group.title,
        items: [
          {
            key: allBucket.key ?? "all",
            label: allBucket.label,
            count: readAdminLegendSummaryCount(
              group.summary,
              allBucket.summaryKey ?? ("total" as Extract<keyof TSummary, string>),
            ),
            semantic: allBucket.semantic ?? "all",
            active: !group.activeValue,
            onClick: onSelect ? () => onSelect(undefined) : undefined,
          },
          ...group.buckets.map((bucket) => {
            const value = bucket.value;
            return {
              key: bucket.key ?? value,
              label: bucket.label,
              count: readAdminLegendSummaryCount(group.summary, value),
              semantic: bucket.semantic,
              active: group.activeValue === value,
              onClick: onSelect ? () => onSelect(value) : undefined,
            };
          }),
        ],
      };
    }),
  );
}

export function buildAdminSemanticEnumFilterLegendGroups<
  TSummary extends object,
  TValue extends string,
>(groups: AdminSemanticEnumFilterLegendGroupInput<TSummary, TValue>[]): AdminLegendGroup[] {
  return buildAdminLegendGroups(
    groups.map((group) => {
      const allBucket = group.allBucket ?? {
        key: "all",
        summaryKey: "total" as Extract<keyof TSummary, string>,
        semantic: "all" as const,
      };
      const onSelect = group.onSelect;
      const buckets: readonly AdminSemanticEnumFilterLegendBucket<TSummary, TValue>[] | undefined =
        group.buckets ??
        group.values?.map(
          (value): AdminSemanticEnumFilterLegendBucket<TSummary, TValue> => ({
            value,
            summaryKey: group.getSummaryKey
              ? group.getSummaryKey(value)
              : (value as unknown as Extract<keyof TSummary, string>),
          }),
        );
      if (!buckets || buckets.length === 0) {
        throw new Error(
          `ADMIN_LEGEND_ENUM_VALUES_INVALID:${group.key}: expected values or buckets`,
        );
      }
      return {
        key: group.key,
        field: group.field,
        title: group.title,
        items: [
          {
            key: allBucket.key ?? "all",
            label: allBucket.label,
            count: readAdminSemanticEnumLegendSummaryCount(
              group,
              undefined,
              allBucket.summaryKey ?? ("total" as Extract<keyof TSummary, string>),
            ),
            semantic: allBucket.semantic ?? "all",
            active: !group.activeValue,
            onClick: onSelect ? () => onSelect(undefined) : undefined,
          },
          ...buckets.map((bucket) => {
            const value = bucket.value;
            const presentation = readAdminSemanticEnumLegendPresentation(group.registry, value);
            return {
              key: bucket.key ?? value,
              label: group.getLabel(value),
              count: readAdminSemanticEnumLegendSummaryCount(group, value, bucket.summaryKey),
              semantic: presentation.semantic,
              color: presentation.color,
              active: group.activeValue === value,
              onClick: onSelect ? () => onSelect(value) : undefined,
            };
          }),
        ],
      };
    }),
  );
}

export function buildAdminStatusLegendGroups(
  items: AdminSummaryBarItem[],
  title: ReactNode = t("common.nouns.status"),
): AdminLegendGroup[] {
  return buildAdminLegendGroups([{ key: "status", field: "status", title, items }]);
}
